import React, { useState, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useSaveStore from '../store/saveStore';
import { DIFFICULTY_LEVELS } from '../data/difficulty';
import { InteractiveMap } from '../components/game/InteractiveMap';
import { formatMoney } from '../utils/format';
import { AdvisorSelection } from '../components/game/AdvisorSelection';
import LegalFormSelection from '../components/createCompany/LegalFormSelection';
import { useTranslation } from '../i18n';
import { MapPin, ChevronRight, ChevronLeft, Trash2, Upload, Shield, Swords, Zap, ImagePlus, X } from 'lucide-react';
import HSTLogo from '../components/HSTLogo';
import MarkdownLite from '../components/MarkdownLite';
import { getCityBaseRent, HQ_RENT_FREE_DAYS } from '../data/depotGeo';
import { localize } from '../data/i18nData';

// Transport-themed logos — each evokes a different facet of the trade:
// truck, wheel, package, palette, container, road, compass, signal-route.
// All SVGs are 40×40 with white strokes/fills on a coloured rounded square
// background. Designed to read at 32-48 px UI sizes.
const LOGOS = [
  // 1. Side-view truck cab+box
  { id: 1, labelKey: 'logo_truck', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#1F3864" />
      <path d="M6 24h17v-9H6v9z" stroke="white" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
      <path d="M23 18h6l3 3v3h-9" stroke="white" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
      <circle cx="11" cy="26.5" r="2.4" stroke="white" strokeWidth="1.4" fill="#1F3864" />
      <circle cx="27" cy="26.5" r="2.4" stroke="white" strokeWidth="1.4" fill="#1F3864" />
    </>
  )},
  // 2. Truck wheel / pneu
  { id: 2, labelKey: 'logo_wheel', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#0F172A" />
      <circle cx="20" cy="20" r="11" stroke="white" strokeWidth="1.6" fill="none" />
      <circle cx="20" cy="20" r="4.5" stroke="white" strokeWidth="1.4" fill="none" />
      <path d="M20 9v22M9 20h22M12.5 12.5l15 15M27.5 12.5l-15 15" stroke="white" strokeWidth="1.2" />
    </>
  )},
  // 3. Package / Colis
  { id: 3, labelKey: 'logo_package', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#DC2626" />
      <path d="M10 15v12l10 5 10-5V15l-10-5-10 5z" stroke="white" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
      <path d="M10 15l10 5 10-5M20 20v12" stroke="white" strokeWidth="1.6" />
    </>
  )},
  // 4. Palette (Europalette)
  { id: 4, labelKey: 'logo_pallet', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#059669" />
      <path d="M8 18h24v4H8z" stroke="white" strokeWidth="1.4" fill="none" />
      <path d="M11 22v6M20 22v6M29 22v6" stroke="white" strokeWidth="1.5" />
      <path d="M8 30h24" stroke="white" strokeWidth="1.4" />
    </>
  )},
  // 5. Shipping container
  { id: 5, labelKey: 'logo_container', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#7C3AED" />
      <rect x="8" y="14" width="24" height="14" rx="1" stroke="white" strokeWidth="1.6" fill="none" />
      <path d="M12 14v14M16 14v14M20 14v14M24 14v14M28 14v14" stroke="white" strokeWidth="0.9" />
    </>
  )},
  // 6. Compass / Route
  { id: 6, labelKey: 'logo_compass', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#0EA5E9" />
      <circle cx="20" cy="20" r="11" stroke="white" strokeWidth="1.6" fill="none" />
      <path d="M14 26l4-11 4 4 4-1-4 11-4-4-4 1z" fill="white" />
    </>
  )},
  // 7. Highway / Route with milestones
  { id: 7, labelKey: 'logo_road', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#F59E0B" />
      <path d="M14 33L17 7M26 33L23 7" stroke="white" strokeWidth="1.6" />
      <path d="M20 9v3M20 17v3M20 25v3M20 31v2" stroke="white" strokeWidth="2.4" strokeLinecap="round" />
    </>
  )},
  // 8. Signal / Network of routes
  { id: 8, labelKey: 'logo_network', svg: (
    <>
      <rect width="40" height="40" rx="8" fill="#0F766E" />
      <circle cx="20" cy="13" r="2.6" fill="white" />
      <circle cx="11" cy="27" r="2.6" fill="white" />
      <circle cx="29" cy="27" r="2.6" fill="white" />
      <path d="M20 13L11 27M20 13l9 14M11 27h18" stroke="white" strokeWidth="1.5" fill="none" />
    </>
  )},
];

const DIFF_ICONS = { facile: Shield, normal: Zap, difficile: Swords };
const DIFF_COLORS = {
  facile: { border: 'border-emerald-500', bg: 'bg-money/10', text: 'text-money', iconBg: 'bg-money/100' },
  normal: { border: 'border-blue-500', bg: 'bg-accent/10', text: 'text-accent', iconBg: 'bg-accent' },
  difficile: { border: 'border-red-500', bg: 'bg-danger/10', text: 'text-danger', iconBg: 'bg-red-600' },
};

function SaveLoadPanel({ onLoadGame }) {
  const { getSlots, deleteSave } = useSaveStore();
  const { t } = useTranslation();
  const slots = getSlots();
  const hasSaves = slots.some(s => s.save);

  if (!hasSaves) return null;

  return (
    <div className="mt-6 border-t border-ink-600 pt-4" data-testid="save-load-panel">
      <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
        <Upload className="w-4 h-4" /> {t('resume_game')}
      </h3>
      <div className="space-y-2">
        {slots.map(({ key, save }) => {
          if (!save) return null;
          const d = new Date(save.date);
          return (
            <div key={key} className="flex items-center gap-3 bg-ink-700/30 rounded-lg p-3 border border-ink-600" data-testid={`save-${key}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{save.name}</p>
                <p className="text-[10px] text-slate-500">
                  Jour {save.totalDays} — {formatMoney(save.treasury)} — Phase {save.phase} — {save.vehicleCount} veh. / {save.driverCount} chauff.
                </p>
                <p className="text-[10px] text-slate-500">{d.toLocaleDateString('fr-FR')} {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              <button
                onClick={() => onLoadGame(save.data)}
                className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover transition-colors"
                data-testid={`load-${key}`}
              >
                {t('load_save')}
              </button>
              <button
                onClick={() => deleteSave(key)}
                className="text-slate-500 hover:text-danger transition-colors p-1"
                data-testid={`delete-${key}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CreateCompany() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [logo, setLogo] = useState(1);
  const [customLogo, setCustomLogo] = useState(null);
  const [city, setCity] = useState('');
  const [difficulty, setDifficulty] = useState('normal');
  const [advisor, setAdvisor] = useState(null);
  const [legalForm, setLegalForm] = useState('ei'); // Default to EI (balanced)
  // 02/06/2026 — Pop-up explicatif au clic sur "Suivant" si auto-entrepreneur
  // est sélectionné. Le statut reste visible (pédagogie) mais est bloqué :
  // HST recrute immédiatement des chauffeurs salariés, ce qui est incompatible
  // avec le régime micro-entreprise (où le dirigeant exerce seul).
  const [autoBlockedModalOpen, setAutoBlockedModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const createCompany = useGameStore(s => s.createCompany);

  // 14/06/2026 — l'étape « Spécialité » a été retirée (aucun effet gameplay,
  // demande utilisateur). Parcours : Nom → Logo → Ville → Difficulté →
  // Statut juridique → Guide.
  const canNext = () => {
    if (step === 0) return name.length >= 3 && name.length <= 40;
    if (step === 1) return logo > 0 || customLogo;
    if (step === 2) return city !== '';
    if (step === 3) return difficulty !== '';
    if (step === 4) return !!legalForm;
    if (step === 5) return true; // Advisor is optional
    return false;
  };

  const handleFinish = () => {
    createCompany({ name, logo, customLogo, city, difficulty, advisor, legalForm });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert(t('image_too_heavy'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCustomLogo(ev.target.result);
      setLogo(0); // Deselect preset logos
    };
    reader.readAsDataURL(file);
  };

  const removeCustomLogo = () => {
    setCustomLogo(null);
    setLogo(1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLoadGame = (saveData) => {
    // Restore full game state from save
    useGameStore.setState(saveData);
  };

  const steps = [t('step_name'), t('step_logo'), t('step_city'), t('step_difficulty'), 'Statut juridique', t('step_guide')];

  return (
    <div className="min-h-screen bg-ink-900 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-testid="create-company-screen">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1629881635342-c1272d45d0fa?auto=format&fit=crop&w=1600&q=80)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/80 to-background" />
      <div className="absolute inset-0 bg-grid-dots opacity-30" />

      <div className={`relative z-10 w-full ${step === 2 ? 'max-w-4xl' : step === 4 ? 'max-w-3xl' : 'max-w-lg'} transition-[max-width] duration-300`}>
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3">
            <HSTLogo size={72} />
            <div>
              <h1 className="font-display text-5xl font-black text-foreground tracking-tight">HST</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-mono mt-2">{t('cc_company_tagline')}</p>
            </div>
          </div>
        </div>

        <div className="bg-card/95 backdrop-blur-xl border border-ink-600 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-6 pt-5">
            <div className="flex items-center gap-2 mb-1">
              {steps.map((s, i) => (
                <div key={`step-${s}`} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-accent' : 'bg-ink-700'}`} />
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">{t('step')} {step + 1} / {steps.length} — {steps[step]}</p>
          </div>

          <div className="p-6">
            {/* Step 0: Name */}
            {step === 0 && (
              <div className="space-y-4" data-testid="step-name">
                <h2 className="text-xl font-bold text-slate-100">{t('company_name_question')}</h2>
                <p className="text-sm text-slate-500">{t('name_hint')}</p>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Ex : Transports Durand"
                  className="w-full bg-ink-900 border border-ink-600 text-slate-100 placeholder-slate-600 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                  maxLength={40}
                  data-testid="company-name-input"
                />
                <p className="text-xs text-slate-500">{name.length}/40  {t('characters')} {t('min_3')}</p>

                {/* Save/Load panel at bottom of step 0 */}
                <SaveLoadPanel onLoadGame={handleLoadGame} />
              </div>
            )}

            {/* Step 1: Logo */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-logo">
                <h2 className="text-xl font-bold text-slate-100">{t('company_logo')}</h2>
                <p className="text-sm text-slate-500">{t('pick_logo')}</p>

                {/* Upload zone */}
                <div className="border-2 border-dashed border-accent/60 rounded-xl p-4 bg-accent/5 text-center transition-colors hover:border-accent">
                  {customLogo ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative">
                        <img src={customLogo} alt="Logo" className="w-20 h-20 rounded-xl object-contain bg-ink-800 border border-ink-600 shadow-sm" data-testid="custom-logo-preview" />
                        <button
                          onClick={removeCustomLogo}
                          className="absolute -top-2 -right-2 bg-danger/100 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                          data-testid="remove-custom-logo"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-money font-medium">{t('logo_imported_ok')}</p>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center gap-2 py-2">
                      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                        <ImagePlus className="w-6 h-6 text-foreground" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{t('upload_your_logo')}</p>
                      <p className="text-[10px] text-slate-500">{t('upload_format_hint')}</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                    </label>
                  )}
                </div>

                <div className="relative flex items-center gap-3">
                  <div className="flex-1 border-t border-ink-600" />
                  <span className="text-xs text-slate-500">{t('or_pick_template')}</span>
                  <div className="flex-1 border-t border-ink-600" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {LOGOS.map(l => (
                    <button
                      key={l.id}
                      onClick={() => { setLogo(l.id); setCustomLogo(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                        logo === l.id && !customLogo ? 'border-accent bg-accent/10' : 'border-ink-600 hover:border-ink-500'
                      }`}
                      data-testid={`logo-option-${l.id}`}
                    >
                      <svg width="40" height="40" viewBox="0 0 40 40">{l.svg}</svg>
                      <span className="text-xs text-slate-400">{l.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: City */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-city">
                <h2 className="text-xl font-bold text-slate-100">{t('starting_city')}</h2>
                <p className="text-sm text-slate-500">{t('city_pick_hint')}</p>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
                  {/* Cards grid (left on lg, top on mobile) */}
                  <div className="grid grid-cols-2 gap-2 self-start">
                    {[
                      { id: 'paris', name: 'Paris', region: 'Île-de-France' }, // i18n-keep
                      { id: 'lyon', name: 'Lyon', region: 'Auv.-Rhône-Alpes' }, // i18n-keep
                      { id: 'marseille', name: 'Marseille', region: 'PACA' },
                      { id: 'bordeaux', name: 'Bordeaux', region: 'N.-Aquitaine' }, // i18n-keep
                      { id: 'lille', name: 'Lille', region: 'Hauts-de-France' }, // i18n-keep
                    ].map(c => (
                      <button
                        key={c.id}
                        onClick={() => setCity(c.id)}
                        className={`text-left py-2 px-2.5 rounded-lg border-2 transition-all ${
                          city === c.id
                            ? 'border-accent bg-accent/15'
                            : 'border-ink-600 hover:border-ink-500 bg-ink-700/30'
                        }`}
                        data-testid={`city-${c.id}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <MapPin className={`w-3.5 h-3.5 ${city === c.id ? 'text-accent' : 'text-slate-500'}`} />
                          <p className={`text-sm font-semibold ${city === c.id ? 'text-foreground' : 'text-slate-200'}`}>{c.name}</p>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5 ml-5">{c.region}</p>
                        {/* 14/06/2026 — loyer du QG affiché par ville (demande
                            utilisateur : « indiquer les loyers des villes choisies »). */}
                        <p className="text-[10px] font-mono text-amber-300/90 mt-0.5 ml-5" data-testid={`city-rent-${c.id}`}>
                          {t('cc_city_rent', { rent: Math.round(getCityBaseRent(c.id) * 0.25).toLocaleString('fr-FR') })}
                        </p>
                      </button>
                    ))}
                  </div>

                  {/* Interactive map (right on lg, bottom on mobile) */}
                  <div className="rounded-lg border border-ink-600 overflow-hidden bg-ink-900 h-[280px] lg:h-[320px]" data-testid="city-pick-map">
                    <InteractiveMap
                      hqCityId={city || null}
                      phase={1}
                      activeContracts={[]}
                      vehicles={[]}
                      drivers={[]}
                      selectable
                      onSelectCity={setCity}
                      selectableFilter={(c) => c.phase === 1 && c.country === 'FR'}
                      showRoutesPanel={false}
                    />
                  </div>
                </div>

                {city && (
                  <div className="bg-accent/10 rounded-lg p-3 text-center">
                    <p className="font-semibold text-foreground capitalize">{city}</p>
                    <p className="text-xs text-slate-500">Zone locale — bonus −10 % carburant dans un rayon 200 km</p>
                    <p className="text-xs text-amber-300 mt-1" data-testid="city-rent-summary">
                      {t('cc_city_rent', { rent: Math.round(getCityBaseRent(city) * 0.25).toLocaleString('fr-FR') })} — {t('cc_city_rent_free', { days: HQ_RENT_FREE_DAYS })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Difficulty */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-difficulty">
                <h2 className="text-xl font-bold text-slate-100">{t('step_difficulty')}</h2>
                <p className="text-sm text-slate-500">{t('pick_difficulty')}</p>
                <div className="space-y-3">
                  {Object.values(DIFFICULTY_LEVELS).map(rawD => {
                    const d = localize('difficulty', rawD);
                    const Icon = DIFF_ICONS[d.id];
                    const colors = DIFF_COLORS[d.id];
                    const isSelected = difficulty === d.id;
                    return (
                      <button
                        key={d.id}
                        onClick={() => setDifficulty(d.id)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          isSelected ? `${colors.border} ${colors.bg}` : 'border-ink-600 hover:border-ink-500'
                        }`}
                        data-testid={`difficulty-${d.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isSelected ? `${colors.iconBg} text-foreground` : 'bg-ink-700/40 text-slate-400'}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <p className={`font-semibold ${isSelected ? colors.text : 'text-slate-100'}`}>{d.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{d.description}</p>
                            <div className="flex gap-3 mt-2 text-[10px]">
                              <span className={d.revenueMultiplier > 1 ? 'text-money' : d.revenueMultiplier < 1 ? 'text-danger' : 'text-slate-500'}>
                                Revenus {d.revenueMultiplier > 1 ? '+' : ''}{Math.round((d.revenueMultiplier - 1) * 100)}%
                              </span>
                              <span className={d.expenseMultiplier < 1 ? 'text-money' : d.expenseMultiplier > 1 ? 'text-danger' : 'text-slate-500'}>
                                Charges {d.expenseMultiplier > 1 ? '+' : ''}{Math.round((d.expenseMultiplier - 1) * 100)}%
                              </span>
                              <span className="text-slate-500">
                                Capital {formatMoney(d.startingTreasury + d.loanAmount - 22000)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Legal Form Selection */}
            {step === 4 && (
              <LegalFormSelection selected={legalForm} onSelect={setLegalForm} />
            )}

            {/* Step 5: Advisor Selection */}
            {step === 5 && (
              <AdvisorSelection selectedAdvisor={advisor} onSelect={setAdvisor} />
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              {step > 0 ? (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-300"
                  data-testid="btn-prev-step"
                >
                  <ChevronLeft className="w-4 h-4" /> {t('back')}
                </button>
              ) : <div />}

              {step < 5 ? (
                <button
                  onClick={() => {
                    // 02/06/2026 — Intercepter sur l'étape statut juridique
                    // si auto-entrepreneur sélectionné → pop-up pédagogique.
                    if (step === 4 && legalForm === 'auto') {
                      setAutoBlockedModalOpen(true);
                      return;
                    }
                    setStep(step + 1);
                  }}
                  disabled={!canNext()}
                  className="flex items-center gap-1 bg-accent text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent-hover transition-colors"
                  data-testid="btn-next-step"
                >
                  {t('next')} <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={!canNext()}
                  className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700 transition-colors"
                  data-testid="btn-create-company"
                >
                  {t('launch_game')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 02/06/2026 — AutoEntrepreneur incompatibility explanatory modal */}
      {autoBlockedModalOpen && (
        <AutoBlockedModal
          onClose={() => setAutoBlockedModalOpen(false)}
          onChooseEI={() => { setLegalForm('ei'); setAutoBlockedModalOpen(false); setStep(step + 1); }}
          onChooseEURL={() => { setLegalForm('eurl'); setAutoBlockedModalOpen(false); setStep(step + 1); }}
        />
      )}
    </div>
  );
}

/**
 * 02/06/2026 — Pop-up pédagogique affiché si le joueur tente de continuer
 * avec le statut Auto-entrepreneur. Explique que la micro-entreprise est
 * incompatible avec l'embauche immédiate de chauffeurs salariés
 * (le micro-entrepreneur exerce seul, sans salarié).
 */
function AutoBlockedModal({ onClose, onChooseEI, onChooseEURL }) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="auto-blocked-modal"
    >
      <div
        className="bg-ink-800 border-2 border-amber-500/40 rounded-xl max-w-lg w-full p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 border-2 border-amber-500/40 mb-2">
            <span className="text-2xl">👤</span>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-amber-300 font-mono font-bold">
            {t('auto_blocked_kicker')}
          </p>
          <h2 className="font-display text-xl font-black text-foreground mt-1">
            {t('auto_blocked_title')}
          </h2>
        </div>

        <div className="space-y-3 text-sm text-slate-300 leading-relaxed">
          <p><MarkdownLite as="span" text={t('auto_blocked_intro')} /></p>
          <ul className="space-y-1.5 text-[13px] list-disc pl-5">
            <li><MarkdownLite text={t('auto_blocked_bullet_1')} /></li>
            <li><MarkdownLite text={t('auto_blocked_bullet_2')} /></li>
            <li><MarkdownLite text={t('auto_blocked_bullet_3')} /></li>
            <li><MarkdownLite text={t('auto_blocked_bullet_4')} /></li>
          </ul>
          <p className="text-[12px] italic text-slate-400 bg-ink-700/40 border border-ink-600 rounded p-2">
            <MarkdownLite as="span" text={t('auto_blocked_pedagogy')} />
          </p>
        </div>

        <div className="space-y-2 pt-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            {t('auto_blocked_recommended')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onChooseEI}
              className="px-3 py-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-left transition-colors"
              data-testid="auto-blocked-choose-ei"
            >
              <p className="text-xs font-bold text-emerald-200">{t('auto_blocked_choose_ei')}</p>
              <p className="text-[10px] text-slate-400">{t('auto_blocked_choose_ei_desc')}</p>
            </button>
            <button
              onClick={onChooseEURL}
              className="px-3 py-2.5 rounded-lg border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-left transition-colors"
              data-testid="auto-blocked-choose-eurl"
            >
              <p className="text-xs font-bold text-sky-200">{t('auto_blocked_choose_eurl')}</p>
              <p className="text-[10px] text-slate-400">{t('auto_blocked_choose_eurl_desc')}</p>
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-full px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-500 hover:text-slate-300"
            data-testid="auto-blocked-stay"
          >
            {t('auto_blocked_choose_other')}
          </button>
        </div>
      </div>
    </div>
  );
}
